import ProfileManagement from "./components/ProfileManagement/ProfileManagement";
import Home from "./components/Home/Home";
import AgencyDefaults from "./components/AgencyDefaults/AgencyDefaults";
import IvansPolicySync from "./components/IvansPolicySync/IvansPolicySync";
import IvansInitialLoad from "./components/IvansInitialLoad/IvansInitialLoad";
import ReportPage from "./components/Reports/ReportPage";
import SalesLeaderReportPage from "./components/Reports/SalesLeaderReportPage";
import Dashboard from "./components/Dashboard/SalesLeaderView";
import Error from "./components/Error";
import GeaoInterview from "./components/GeaoOnboarding/GeaoInterview";
import { Route, Routes, useLocation } from "react-router-dom";
import { useContext, useEffect } from "react";
import { UserContext } from "./context/UserContextProvider";
import SessionExpired from "./components/SessionExpired/SessionExpired";
import SweepsAccountManagement from "./components/SweepsAccountManagement/SweepsAccountManagement";
import { AgentAccessContext } from "./context/AgentAccessContextProvider";
import ManageCommissionBankingDetails from './components/GeaoOnboarding/GeaoCommissionBankingDetails';
import GeaoCommissionTaxForm from "./components/GeaoOnboarding/GeaoCommissionTaxForm";
import { GeaoRoutes } from "./components/GeaoOnboarding/constants/GeaoRoutes";
import { GeaoPaths } from "./components/GeaoOnboarding/constants/GeaoPaths";
import CreateYourProfileNationalAccount from "./components/GeaoOnboarding/components/CreateYourProfile/CreateYourProfileNationalAccount";
import EmailSettings from "./components/SweepsAccountManagement/EmailSettings";
import SweepAccountSettings from "./components/SweepsAccountManagement/SweepAccountSettings";
import { ClientConfigurationContext } from './context/ClientConfigurationContextProvider';
import AgentTerminationForm from "./components/AgentTermination/AgentTerminationForm";
import ClientAlertEmailPreferencesForm from "./components/ClientAlertEmailPreferences/ClientAlertEmailPreferencesForm";
import ManageBindingAuthority from "./components/ManageBindingAuthority/ManageBindingAuthority";
import OnboardingInteractionTracking, { getOnboardingPageName } from "./components/GeaoOnboarding/OnboardingInteractionTracking";
import { GtmPageViewProvider } from "./context/GtmPageViewContext";

const ScrollToTop = () => {
    const { pathname } = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    return null;
}

export const AppRoutes = [
    ...GeaoRoutes,
    {
        authorize: (user, agentAccess, clientConfiguration) => user.authorizations?.canViewAgencyIdentity && agentAccess?.authorizations?.includes("CanViewAgencyIdentity") && !clientConfiguration?.enableSweepOnlyMode,
        index: true,
        element: <Home />
    },
    {
        authorize: (user, agentAccess, clientConfiguration) => user.authorizations?.canViewAgencyIdentity && agentAccess?.authorizations?.includes("CanViewAgencyIdentity") && clientConfiguration?.enableSweepOnlyMode,
        index: true,
        element: <ProfileManagement />
    },
    {
        authorize: (user, agentAccess) => user.authorizations?.canViewAgencyIdentity && agentAccess?.authorizations?.includes("CanViewAgencyIdentity"),
        path: '/profiles',
        element: <ProfileManagement />
    },
    {
        authorize: (user, agentAccess, clientConfiguration) => clientConfiguration.enableAgencyDefaults && user.authorizations?.canSetAgencyDefaults,
        path: '/agency-defaults-management',
        element: <Home tabName="Agency Defaults Management" />
    },
    {
        authorize: (user, agentAccess) => user.authorizations?.canViewAgencyIdentity && agentAccess?.authorizations?.includes("CanViewAgencyIdentity"),
        path: '/defaults',
        element: <AgencyDefaults />
    },
    {
        authorize: (user, agentAccess, clientConfiguration) => clientConfiguration.enableIvansRequest && user.authorizations?.canManageIvansRequests,
        path: '/ivans-request',
        element: <Home tabName="Ivans Request" />
    },
    {
        authorize: (user, agentAccess) => user.authorizations?.canViewAgencyIdentity && agentAccess?.authorizations?.includes("CanViewAgencyIdentity"),
        path: '/policy-sync',
        element: <IvansPolicySync />
    },
    {
        authorize: (user, agentAccess) => user.authorizations?.canViewAgencyIdentity && agentAccess?.authorizations?.includes("CanViewAgencyIdentity"),
        path: '/initial-load',
        element: <IvansInitialLoad />
    },
    {
        authorize: (user, agentAccess, clientConfiguration) => clientConfiguration.enableBankTab && user.authorizations?.canManageBanking,
        path: '/bank-info',
        element: <Home tabName="Bank and W-9 Info" />
    },
    {
        authorize: (user, agentAccess, clientConfiguration) => user.authorizations?.canAccessReports && agentAccess?.authorizations?.includes("CanViewExternalReports") && clientConfiguration?.enableReports,
        path: '/reports',
        element: <ReportPage />
    },
    {
        authorize: (user, agentAccess, clientConfiguration) => clientConfiguration.enableAdminTab && user.authorizations?.canManageAdmin,
        path: '/admin',
        element: <Home tabName="Admin" />
    },
    {
        authorize: (user, agentAccess, clientConfiguration) => user.authorizations?.canViewAgencyIdentity && clientConfiguration?.enableReportsSalesLeader,
        path: '/view-reports',
        element: <SalesLeaderReportPage />
    },
    {
        authorize: (user, agentAccess, clientConfiguration) => user.authorizations?.canViewAgencyIdentity && clientConfiguration?.enableReportsSalesLeader,
        path: '/dashboard',
        element: <Dashboard />
    },
    {
        authorize: _ => true,
        path: '/onboarding-interview',
        element: <GeaoInterview />
    },
    {
        authorize: (user, agentAccess) => user.authorizations?.canViewAgencyIdentity && agentAccess?.authorizations?.includes("CanViewAgencyIdentity"),
        path: '/onboarding-commission-tax-form',
        element: <GeaoCommissionTaxForm />
    },
    {
        authorize: (user) => user.sessionExpired || user.loginRequired,
        path: "*",
        element: <SessionExpired />
    },
    {
        authorize: (user) => !(user.sessionExpired || user.loginRequired),
        path: "*",
        element: <Error />
    },
    {
        authorize: (user, agentAccess) => user.authorizations?.canViewAgencyIdentity && agentAccess?.authorizations?.includes("CanViewAgencyIdentity"),
        path: '/sweep-account-management',
        element: <SweepsAccountManagement />
    },
    {
        authorize: (user, agentAccess, clientConfiguration) => clientConfiguration?.enableBankTab && user.authorizations?.canManageBanking,
        path: '/commission-banking-details',
        element: <ManageCommissionBankingDetails />
    },
    {
        authorize: _ => true,
        path: '/email-settings',
        element: <EmailSettings />
    },
    {
        authorize: _ => true,
        path: '/sweep-account-settings',
        element: <SweepAccountSettings />
    },
    {
        authorize: (user, agentAccess, clientConfiguration) => user.authorizations?.canManageAdmin && user.authorizations?.canTerminateAgent && clientConfiguration?.displayAgentTerminationRequest,
        path: '/agent-termination',
        element: <AgentTerminationForm />
    },
    {
        authorize: (user, agentAccess, clientConfiguration) => user.authorizations?.canManageAdmin && user.authorizations?.canManageClientPreferences && clientConfiguration?.displayClientAlertEmailPreferences,
        path: '/client-alert-email-preferences',
        element: <ClientAlertEmailPreferencesForm />
    },
    {
        authorize: (user, agentAccess, clientConfiguration) => user.authorizations?.canManageAdmin && user.authorizations?.canSetBindingAuthority && clientConfiguration?.displayManageBindingAuthority,
        path: '/manage-binding-authority',
        element: <ManageBindingAuthority />
    }
];

export default function AppRouter() {
    const user = useContext(UserContext);
    const agentAccess = useContext(AgentAccessContext);
    const clientConfiguration = useContext(ClientConfigurationContext);

    // Generate one route per cobranded national account from the feature flag.
    // Adding or removing accounts in the flag requires no code change here.
    const cobrandedNationalAccountRoutes = (clientConfiguration?.cobrandedNationalAccounts ?? []).map(account => ({
        authorize: () => true,
        path: `${GeaoPaths.PreOnboardingNationalAccountPrefix}${account.slug}`,
        element: <CreateYourProfileNationalAccount />
    }));

    const routes = [...cobrandedNationalAccountRoutes, ...AppRoutes];

    return <>
        <ScrollToTop />
        <GtmPageViewProvider>
        <Routes>
            {routes
                .filter(route => route.authorize(user, agentAccess, clientConfiguration))
                .map(route => {
                    const pageName = getOnboardingPageName(route.path);
                    const routeKey = route.path ?? `index:${route.element?.type?.name ?? route.element?.type?.displayName ?? 'route'}`;
                    return <Route key={routeKey} {...route} element={pageName
                        ? <OnboardingInteractionTracking pageName={pageName}>{route.element}</OnboardingInteractionTracking>
                        : route.element} />;
                })}
        </Routes>
        </GtmPageViewProvider>
    </>
}
